/**
 * Dependency Graph Component
 * Visualizes entity relationships and dependencies
 * 
 * Uses a simple force-directed layout for node positioning
 */

import React, { useEffect, useRef } from 'react';
import './DependencyGraph.css';

const DependencyGraph = ({ graph }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;

    // Simple force-directed layout
    const nodes = graph.nodes.map((node, i) => ({
      ...node,
      x: width / 2 + Math.random() * 200 - 100,
      y: height / 2 + Math.random() * 200 - 100,
      vx: 0,
      vy: 0,
      radius: 30
    }));

    const edges = graph.edges.map(edge => ({
      ...edge,
      sourceNode: nodes.find(n => n.title === edge.source),
      targetNode: nodes.find(n => n.title === edge.target)
    })).filter(e => e.sourceNode && e.targetNode);

    // Animation loop
    let animationId;
    let iteration = 0;
    const maxIterations = 300;

    function animate() {
      if (iteration++ > maxIterations) return;

      // Apply forces
      nodes.forEach(node => {
        node.vx *= 0.9;
        node.vy *= 0.9;

        // Center force
        const centerX = width / 2;
        const centerY = height / 2;
        node.vx += (centerX - node.x) * 0.001;
        node.vy += (centerY - node.y) * 0.001;

        // Repulsion between nodes
        nodes.forEach(other => {
          if (node === other) return;
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          if (dist < 200) {
            const force = (200 - dist) / dist * 0.5;
            node.vx += dx * force;
            node.vy += dy * force;
          }
        });
      });

      // Attraction along edges
      edges.forEach(edge => {
        const dx = edge.targetNode.x - edge.sourceNode.x;
        const dy = edge.targetNode.y - edge.sourceNode.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - 150) / dist * 0.1;
        
        edge.sourceNode.vx += dx * force;
        edge.sourceNode.vy += dy * force;
        edge.targetNode.vx -= dx * force;
        edge.targetNode.vy -= dy * force;
      });

      // Update positions
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;

        // Boundary collision
        node.x = Math.max(node.radius, Math.min(width - node.radius, node.x));
        node.y = Math.max(node.radius, Math.min(height - node.radius, node.y));
      });

      // Draw
      ctx.clearRect(0, 0, width, height);

      // Draw edges
      edges.forEach(edge => {
        const source = edge.sourceNode;
        const target = edge.targetNode;

        // Draw line
        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        
        // Color based on rule type and condition
        if (edge.ruleType === 'Required') {
          ctx.strokeStyle = edge.conditionMet ? '#107C10' : '#E81123';
        } else if (edge.ruleType === 'Blocks') {
          ctx.strokeStyle = edge.conditionMet ? '#E81123' : '#999';
        } else { // Contributes
          ctx.strokeStyle = edge.conditionMet ? '#0078D4' : '#FFB900';
        }
        
        ctx.lineWidth = edge.conditionMet ? 3 : 2;
        ctx.setLineDash(edge.conditionMet ? [] : [5, 5]);
        ctx.stroke();

        // Draw arrow
        const angle = Math.atan2(target.y - source.y, target.x - source.x);
        const arrowSize = 10;
        const arrowX = target.x - Math.cos(angle) * target.radius;
        const arrowY = target.y - Math.sin(angle) * target.radius;
        
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(
          arrowX - arrowSize * Math.cos(angle - Math.PI / 6),
          arrowY - arrowSize * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          arrowX - arrowSize * Math.cos(angle + Math.PI / 6),
          arrowY - arrowSize * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fillStyle = ctx.strokeStyle;
        ctx.fill();
      });

      // Draw nodes
      nodes.forEach(node => {
        // Node circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        
        // Color based on entity type
        const colors = {
          Metric: '#0078D4',
          Project: '#FFB900',
          CSF: '#107C10',
          Constraint: '#E81123',
          Initiative: '#8B4789'
        };
        ctx.fillStyle = colors[node.type] || '#666';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Node label
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Wrap text if too long
        const maxWidth = node.radius * 1.8;
        const words = node.title.split(' ');
        let line = '';
        let y = node.y;
        
        words.forEach(word => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && line !== '') {
            ctx.fillText(line, node.x, y);
            line = word + ' ';
            y += 12;
          } else {
            line = testLine;
          }
        });
        ctx.fillText(line, node.x, y);
      });

      animationId = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [graph]);

  if (!graph || !graph.nodes || graph.nodes.length === 0) {
    return (
      <div className="dependency-graph-empty">
        <p>No dependency relationships defined.</p>
        <p>Add dependency rules to visualize entity connections.</p>
      </div>
    );
  }

  return (
    <div className="dependency-graph-container">
      <canvas ref={canvasRef} className="dependency-graph-canvas" />
      
      <div className="graph-legend">
        <h4>Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-line required"></div>
            <span>Required (met)</span>
          </div>
          <div className="legend-item">
            <div className="legend-line required-failed"></div>
            <span>Required (not met)</span>
          </div>
          <div className="legend-item">
            <div className="legend-line contributes"></div>
            <span>Contributes (met)</span>
          </div>
          <div className="legend-item">
            <div className="legend-line contributes-failed"></div>
            <span>Contributes (not met)</span>
          </div>
          <div className="legend-item">
            <div className="legend-line blocks"></div>
            <span>Blocks (active)</span>
          </div>
        </div>
        
        <h4>Entity Types</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-node metric"></div>
            <span>Metric</span>
          </div>
          <div className="legend-item">
            <div className="legend-node project"></div>
            <span>Project</span>
          </div>
          <div className="legend-item">
            <div className="legend-node csf"></div>
            <span>CSF</span>
          </div>
          <div className="legend-item">
            <div className="legend-node constraint"></div>
            <span>Constraint</span>
          </div>
          <div className="legend-item">
            <div className="legend-node initiative"></div>
            <span>Initiative</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DependencyGraph;